import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout from "../../components/Layout";
import api from "../../lib/api";
import { toast } from "sonner";
import { FileText, Plus, Trash2, Loader2, Archive, ArchiveRestore, ArrowUp, ArrowDown, ArrowUpDown, AlertTriangle } from "lucide-react";
import InfiniteSentinel from "../../components/InfiniteSentinel";
import LiveCount from "../../components/LiveCount";
import { OwnerChip } from "../../components/OwnerChip";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";

function formatDate(v) {
  if (!v) return "—";
  const d = new Date(String(v));
  if (isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short", year: "numeric" });
}

const STATUS_COLORS = {
  "Draft":            "bg-slate-500/15 text-slate-300 ring-slate-500/30",
  "Pending Approval": "bg-amber-500/15 text-amber-300 ring-amber-500/30",
  "Sent":             "bg-blue-500/15 text-blue-300 ring-blue-500/30",
  "Accepted":         "bg-emerald-500/15 text-emerald-300 ring-emerald-500/30",
  "Rejected":         "bg-red-500/15 text-red-300 ring-red-500/30",
  "Expired":          "bg-orange-500/15 text-orange-300 ring-orange-500/30",
  "Invoiced":         "bg-indigo-500/15 text-indigo-300 ring-indigo-500/30",
  "Archived":         "bg-zinc-500/15 text-zinc-400 ring-zinc-500/30",
};
function StatusBadge({ value }) {
  const cls = STATUS_COLORS[value] || "bg-slate-500/15 text-slate-300 ring-slate-500/30";
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ring-1 ${cls}`}>{value || "—"}</span>;
}

function QuoteAlertFlag({ info }) {
  if (!info) return null;
  return (
    <span className="relative inline-flex group align-middle ml-1.5" data-testid="quote-alert-flag">
      <AlertTriangle className="w-3.5 h-3.5 text-amber-400 notif-flash cursor-help" />
      <span className="pointer-events-none absolute left-0 top-5 z-30 hidden group-hover:block w-72 rounded-lg bg-[#0B1120] border border-amber-500/30 shadow-xl p-2.5 text-left normal-case">
        <span className="block text-[10px] font-mono uppercase tracking-wider text-amber-300 mb-1.5">
          {info.count} unread update{info.count > 1 ? "s" : ""}{info.actors?.length ? ` · ${info.actors.join(", ")}` : ""}
        </span>
        {(info.items || []).map((it, i) => (
          <span key={i} className="block mb-1.5 last:mb-0">
            <span className="block text-[11.5px] text-white leading-snug font-medium">{it.title}</span>
            {it.body && <span className="block text-[11px] text-slate-400 leading-snug">{it.body}</span>}
          </span>
        ))}
      </span>
    </span>
  );
}

function SortTh({ label, sortKey, activeKey, dir, onSort, align = "left", scope }) {
  const active = activeKey === sortKey;
  return (
    <th className={`px-4 py-3 text-[11px] uppercase tracking-wider text-slate-400 font-medium select-none ${align === "right" ? "text-right" : "text-left"}`}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        data-testid={`quotes-${scope}-th-${sortKey}`}
        className={`inline-flex items-center gap-1 hover:text-white transition-colors ${active ? "text-orange-200" : ""} ${align === "right" ? "flex-row-reverse" : ""}`}
        title={`Sort by ${label}`}
      >
        {label}
        {active
          ? (dir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)
          : <ArrowUpDown className="w-3 h-3 opacity-30" />}
      </button>
    </th>
  );
}

export default function QuotesList({ scope }) {
  const navigate = useNavigate();
  const isMine = scope === "mine";
  const LIMIT = 50;
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [alerts, setAlerts] = useState({});

  // Unread quote-activity flags for this user (flashing exclamation + hover).
  useEffect(() => {
    let alive = true;
    const fetchAlerts = async () => {
      try { const { data } = await api.get("/notifications/by-quote"); if (alive) setAlerts(data || {}); }
      catch { /* non-fatal */ }
    };
    fetchAlerts();
    const t = setInterval(fetchAlerts, 30000);
    return () => { alive = false; clearInterval(t); };
  }, []);
  const onHeaderSort = (key) => {
    if (sortKey !== key) { setSortKey(key); setSortDir("asc"); return; }
    if (sortDir === "asc") { setSortDir("desc"); return; }
    setSortKey(null); setSortDir("asc");
  };
  const sortedItems = useMemo(() => {
    if (!sortKey) return items;
    const val = (r) => {
      switch (sortKey) {
        case "ref":     return (r.QuoteRef || "").toLowerCase();
        case "status":  return (r.Status || "").toLowerCase();
        case "created": return new Date(r.CreatedDate || r._created_at || 0).getTime();
        case "am":      return (r.AccountManager || "").toLowerCase();
        case "by":      return (r.CreatedBy || "").toLowerCase();
        case "expiry":  return new Date(r.ExpiryDate || 0).getTime();
        default:        return 0;
      }
    };
    const dir = sortDir === "asc" ? 1 : -1;
    return [...items].sort((a, b) => {
      const va = val(a), vb = val(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
  }, [items, sortKey, sortDir]);
  const pageRef = useRef(1);
  const loadingRef = useRef(false);
  const genRef = useRef(0);

  const fetchAt = useCallback(async (page, append) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    const gen = genRef.current;
    try {
      const res = await api.get(`/quotes?scope=${scope}&page=${page}&limit=${LIMIT}&include_archived=${showArchived}`);
      if (gen !== genRef.current) return;
      const batch = res.data.items || [];
      const t = res.data.total || 0;
      setItems((prev) => (append ? [...prev, ...batch] : batch));
      setTotal(t);
      pageRef.current = page;
      setHasMore(page * LIMIT < t);
    } catch {
      if (gen === genRef.current) toast.error("Failed to load quotes");
    } finally {
      if (gen === genRef.current) { loadingRef.current = false; setLoading(false); }
    }
  }, [scope, showArchived]);

  const load = useCallback(() => {
    genRef.current += 1;
    pageRef.current = 1;
    loadingRef.current = false;
    setHasMore(true);
    fetchAt(1, false);
  }, [fetchAt]);

  const loadMore = useCallback(() => {
    if (!loadingRef.current && hasMore) fetchAt(pageRef.current + 1, true);
  }, [hasMore, fetchAt]);

  useEffect(() => { load(); }, [load]);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setBusy(true);
    try {
      await api.delete(`/quotes/${encodeURIComponent(deleteTarget._id)}`);
      toast.success("Quote deleted");
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error("Delete failed", { description: e?.response?.data?.detail || "" });
    } finally {
      setBusy(false);
    }
  };

  const toggleArchive = async (row) => {
    const archived = row.Status === "Archived";
    if (!archived && !window.confirm(`Archive quote ${row.QuoteRef || ""}? It will be hidden from active lists (restore any time).`)) return;
    try {
      await api.post(`/quotes/${row._id}/${archived ? "unarchive" : "archive"}`, archived ? {} : { reason: "Archived from Quotes list" });
      toast.success(archived ? "Quote restored" : "Quote archived");
      load();
    } catch (e) {
      toast.error((archived ? "Restore" : "Archive") + " failed", { description: e?.response?.data?.detail || "" });
    }
  };

  return (
    <Layout>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-4xl font-extrabold tracking-tight flex items-center gap-3" data-testid={`quotes-${scope}-title`}>
            {isMine ? "My Quotes" : "All Quotes"}
            <LiveCount shown={items.length} total={total} noun="quote" testId={`quotes-${scope}-live-count`} />
          </h1>
          <p className="text-slate-400 mt-2 text-sm">
            {isMine ? "Quotes you've created." : "Every quote in the system."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => { setShowArchived((v) => !v); }}
            className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-mono uppercase tracking-widest border transition ${
              showArchived
                ? "bg-orange-500/15 border-orange-500/40 text-orange-300"
                : "bg-transparent border-white/10 text-slate-400 hover:bg-white/5"
            }`}
            data-testid={`quotes-${scope}-toggle-archived`}
            aria-pressed={showArchived}
          >
            <Archive className="w-3.5 h-3.5" /> {showArchived ? "Show archived: ON" : "Show archived: OFF"}
          </button>
          <Link to="/quotes/new" className="btn-coral inline-flex items-center gap-2" data-testid={`quotes-${scope}-create-cta`}>
            <Plus className="w-4 h-4" />
            Create Quote
          </Link>
        </div>
      </div>

      <div className="card-dark border border-white/5 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.03] border-b border-white/5">
              <tr>
                <SortTh label="Quote Ref"       sortKey="ref"     activeKey={sortKey} dir={sortDir} onSort={onHeaderSort} scope={scope} />
                <SortTh label="Status"          sortKey="status"  activeKey={sortKey} dir={sortDir} onSort={onHeaderSort} scope={scope} />
                <SortTh label="Created Date"    sortKey="created" activeKey={sortKey} dir={sortDir} onSort={onHeaderSort} scope={scope} />
                <SortTh label="Account Manager" sortKey="am"      activeKey={sortKey} dir={sortDir} onSort={onHeaderSort} scope={scope} />
                <SortTh label="Created By"      sortKey="by"      activeKey={sortKey} dir={sortDir} onSort={onHeaderSort} scope={scope} />
                <SortTh label="Expiry Date"     sortKey="expiry"  activeKey={sortKey} dir={sortDir} onSort={onHeaderSort} scope={scope} />
                <th className="text-right px-4 py-3 text-[11px] uppercase tracking-wider text-slate-400 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading...</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-slate-500" data-testid={`quotes-${scope}-empty`}>
                    <FileText className="w-6 h-6 mx-auto mb-2 opacity-40" />
                    No quotes {showArchived ? "match — try turning off Show archived" : "yet"}.{" "}
                    {!showArchived && (
                      <button className="text-orange-400 hover:underline" onClick={() => navigate("/quotes/new")} data-testid={`quotes-${scope}-empty-create`}>Create your first quote</button>
                    )}
                  </td>
                </tr>
              )}
              {sortedItems.map((row) => {
                const isArchived = row.Status === "Archived";
                return (
                  <tr key={row._id} className={`border-t border-white/5 hover:bg-white/[0.02] ${isArchived ? "opacity-60" : ""}`} data-testid={`quotes-${scope}-row-${row._id}`}>                    <td className="px-4 py-3 font-mono text-xs text-slate-100 font-medium" data-testid={`quotes-${scope}-ref-${row._id}`}>
                      <Link to={`/quotes/${row._id}/edit`} className="hover:text-orange-300 hover:underline">
                        {row.QuoteRef || "—"}
                      </Link>
                      <QuoteAlertFlag info={alerts[row._id]} />
                    </td>
                    <td className="px-4 py-3"><StatusBadge value={row.Status} /></td>
                    <td className="px-4 py-3 text-slate-300 text-xs whitespace-nowrap">{formatDate(row.CreatedDate || row._created_at)}</td>
                    <td className="px-4 py-3"><OwnerChip primary={row.AccountManager} /></td>
                    <td className="px-4 py-3 text-slate-200">{row.CreatedBy || "—"}</td>
                    <td className="px-4 py-3 text-slate-300 text-xs whitespace-nowrap">{formatDate(row.ExpiryDate)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => navigate(`/quotes/${row._id}/edit`)}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs text-slate-200 hover:bg-white/5 border border-white/10"
                          data-testid={`quotes-${scope}-row-open-${row._id}`}
                        ><FileText className="w-3.5 h-3.5" /> Open</button>
                        <button
                          type="button"
                          onClick={() => toggleArchive(row)}
                          className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs border ${
                            isArchived
                              ? "text-emerald-300 hover:bg-emerald-500/10 border-emerald-500/20"
                              : "text-amber-300 hover:bg-amber-500/10 border-amber-500/20"
                          }`}
                          data-testid={`quotes-${scope}-row-${isArchived ? "restore" : "archive"}-${row._id}`}
                          title={isArchived ? "Restore this quote" : "Archive this quote"}
                        >
                          {isArchived ? <><ArchiveRestore className="w-3.5 h-3.5" /> Restore</> : <><Archive className="w-3.5 h-3.5" /> Archive</>}
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleteTarget(row)}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs text-red-300 hover:bg-red-500/10 border border-red-500/20"
                          data-testid={`quotes-${scope}-row-delete-${row._id}`}
                        ><Trash2 className="w-3.5 h-3.5" /> Delete</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {items.length > 0 && (
          <InfiniteSentinel
            onReach={loadMore}
            hasMore={hasMore}
            loading={loading}
            count={items.length}
            total={total}
            testId={`quotes-${scope}-sentinel`}
          />
        )}
      </div>

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !busy && !v && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-[#0F1420] border-white/10 text-slate-100">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-xl text-white">Delete this quote?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              This moves quote <span className="font-mono text-slate-200">{deleteTarget?.QuoteRef || deleteTarget?._id?.slice(0, 8) + "…"}</span> to the bin. You can restore it from View Quotes → Archived &amp; deleted.
              <br />
              <span className="text-amber-300 text-xs">Tip: use Archive instead to hide it from active lists without losing the record.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border border-white/10 text-slate-300 hover:bg-white/5">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} disabled={busy} className="bg-red-500 hover:bg-red-600 text-white">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
